// Composite hook for the /admin homepage KPI row. Each KPI lives in
// its own useEffect so a slow/failed call on one card never stalls the
// others — the row degrades per-cell. Each cell exposes a `retry()` so
// the user can re-fire that fetch alone.
//
// Every card auto-refreshes every 30s (KPI_POLL_MS) with an in-flight
// guard, mirroring useBadgeCounts — plus a visibility pause that hook
// doesn't have: hidden tabs skip ticks and refetch immediately on
// return, so forgotten background dashboards don't poll the BE.
import { useCallback, useEffect, useRef, useState } from "react";
import {
    fetchUnfulfilledCount, fetchFulfilledToday,
    fetchActiveToday, fetchTotesToPutAway,
    fetchInPickingCount, fetchInSortingCount, fetchAwaitingDispatchCount,
    fetchSorterWorkload,
    OrderStageCounts, SortingCounts, FulfilmentMode,
} from "../api/adminDashboard";

export type CardState = "loading" | "ok" | "error";

export interface KpiState<T> {
    state: CardState;
    value: T | null;
    retry: () => void;
}

export interface AdminDashboardKpis {
    unfulfilled: KpiState<OrderStageCounts>;
    inPicking: KpiState<OrderStageCounts>;
    inSorting: KpiState<SortingCounts>;
    awaitingDispatch: KpiState<OrderStageCounts>;
    fulfilledToday: KpiState<OrderStageCounts>;
    activeToday: KpiState<number>;
    totesToPutAway: KpiState<number>;
    /** Pick & Sort or Sorter 1 / Sorter 2 — decides which pipeline cards
     *  the row shows. null until the sorting fetch has answered for a
     *  warehouse this browser has never seen. */
    fulfilmentMode: FulfilmentMode | null;
}

export const KPI_POLL_MS = 30_000;

// The fulfilment mode is learnt from the sorting fetch, but the row renders
// before that answers. Remembering the last answer per warehouse means a
// sorter warehouse paints without the picking card from the first frame
// instead of showing it as a skeleton and then pulling it. Per-viewer
// convenience only: a missing or blocked store just means one extra fetch.
export const FULFILMENT_MODE_STORAGE_PREFIX = "portal.admin.kpi.fulfilmentMode:";

const readRememberedMode = (warehouse: string): FulfilmentMode | null => {
    if (!warehouse) return null;
    try {
        const v = window.localStorage.getItem(FULFILMENT_MODE_STORAGE_PREFIX + warehouse);
        return v === "sorters" || v === "pick_and_sort" ? v : null;
    } catch {
        return null;
    }
};

const rememberMode = (warehouse: string, mode: FulfilmentMode): void => {
    try {
        window.localStorage.setItem(FULFILMENT_MODE_STORAGE_PREFIX + warehouse, mode);
    } catch {
        // Nothing to do — the next load simply learns the mode again.
    }
};

const readWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return "";
        const parsed = JSON.parse(raw);
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : "";
    } catch {
        return "";
    }
};

// Generic per-card fetcher hook. Bumps an internal nonce when retry()
// is called, which re-runs the effect (back to the loading skeleton).
//
// Error semantics:
//   - initial load fails       → "error" + retry button
//   - poll fails after success → keep last-good value, stay "ok"
//     (no flicker on a transient blip)
//   - a card in "error" self-heals: ticks keep firing regardless of
//     state, so a later success flips it back to "ok"
const useKpi = <T,>(load: () => Promise<T>, deps: unknown[]): KpiState<T> => {
    const [state, setState] = useState<CardState>("loading");
    const [value, setValue] = useState<T | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let alive = true;
        let inFlight = false;
        let hasValue = false;
        setState("loading");
        setValue(null);

        const run = async () => {
            if (inFlight) return;
            inFlight = true;
            try {
                const v = await load();
                if (!alive) return;
                hasValue = true;
                setValue(v);
                setState("ok");
            } catch {
                if (alive && !hasValue) setState("error");
            } finally {
                inFlight = false;
            }
        };

        run();
        const id = window.setInterval(() => {
            if (document.visibilityState === "hidden") return;
            run();
        }, KPI_POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") run();
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            alive = false;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, nonce]);

    const retry = useCallback(() => setNonce((n) => n + 1), []);
    return { state, value, retry };
};

const EMPTY_STAGE: OrderStageCounts = { orders: 0, items: 0 };

export const useAdminDashboard = (): AdminDashboardKpis => {
    const warehouse = readWarehouse();
    const remembered = readRememberedMode(warehouse);
    // The picking loader reads the mode through a ref: keying its effect on
    // the mode would reset that card to a skeleton every time it changed.
    const modeRef = useRef<FulfilmentMode | null>(remembered);

    const unfulfilled = useKpi(
        async () => warehouse ? fetchUnfulfilledCount(warehouse) : EMPTY_STAGE,
        [warehouse],
    );

    // Sorter warehouses (Newport, Poland) do not pick — the card is hidden
    // there, so its count is not worth a request once the mode is known.
    const inPicking = useKpi(
        async () => (warehouse && modeRef.current !== "sorters") ? fetchInPickingCount(warehouse) : EMPTY_STAGE,
        [warehouse],
    );

    // Sorter warehouses have no orders.status = 'sorting' — their sorting
    // work is Sorter 1 + Sorter 2 (zone storage). Ask for that first; the
    // BE answers { sorters: false } for a Pick & Sort site and the card
    // falls back to the orders-table count. Either way this fetch is what
    // teaches the row which warehouse type it is looking at.
    const inSorting = useKpi<SortingCounts>(
        async () => {
            if (!warehouse) return { ...EMPTY_STAGE, mode: "pick_and_sort" };
            let sorters: SortingCounts | null = null;
            try {
                sorters = await fetchSorterWorkload(warehouse);
            } catch (err) {
                // A sorter warehouse has nothing else to show, so that is the
                // card's error. Anywhere else (or a warehouse this browser
                // has never seen) falls through to the orders-table count.
                if (modeRef.current === "sorters") throw err;
            }
            const value: SortingCounts = sorters
                ?? { ...(await fetchInSortingCount(warehouse)), mode: "pick_and_sort" };
            modeRef.current = value.mode;
            rememberMode(warehouse, value.mode);
            return value;
        },
        [warehouse],
    );

    const awaitingDispatch = useKpi(
        async () => warehouse ? fetchAwaitingDispatchCount(warehouse) : EMPTY_STAGE,
        [warehouse],
    );

    const fulfilledToday = useKpi(
        async () => warehouse ? fetchFulfilledToday(warehouse) : EMPTY_STAGE,
        [warehouse],
    );

    const activeToday = useKpi(
        async () => fetchActiveToday(warehouse),
        [warehouse],
    );

    const totesToPutAway = useKpi(
        async () => fetchTotesToPutAway(warehouse),
        [warehouse],
    );

    return {
        unfulfilled, inPicking, inSorting, awaitingDispatch,
        fulfilledToday, activeToday, totesToPutAway,
        fulfilmentMode: inSorting.value?.mode ?? remembered,
    };
};
